import CommunityFollowButton from '@atoms/CommunityFollowButton';
import CommunityGradientBanner from '@atoms/CommunityGradientBanner';
import Colors from '@constants/Colors';
import ProfileSiteButtons from '@molecules/ProfileSiteButtons';
import { type Community } from '@sunrise-ui/api/profile';
import {
  Button,
  ButtonVariants,
  ExpandableText,
  MANAGER_APP_URL,
  ProfileImg,
  TextBold,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React, { forwardRef, useMemo } from 'react';
import { Linking, StyleSheet, View } from 'react-native';

interface CommunityHeaderProps {
  community?: Community;
  isLoadingCommunity: boolean;
}

function CommunityHeaderComponent(
  { community, isLoadingCommunity }: CommunityHeaderProps,
  ref: React.ForwardedRef<View>,
) {
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);

  // Check if user has permission to manage this community
  const { isAuthenticated, ownedCommunityIds, editorCommunityIds } =
    useAuthStore();
  const canManageCommunity = useMemo(() => {
    if (!isAuthenticated || !community?.id) return false;
    return (
      ownedCommunityIds?.includes(community.id) ||
      editorCommunityIds?.includes(community.id)
    );
  }, [isAuthenticated, community?.id, ownedCommunityIds, editorCommunityIds]);

  const handleManagePress = () => {
    void Linking.openURL(MANAGER_APP_URL);
  };

  const hasSocialLinks =
    community?.links?.linkedin ||
    community?.links?.github ||
    community?.links?.x ||
    community?.links?.website;

  return (
    <View style={styles.container} ref={ref}>
      {/* Gradient Banner */}
      <CommunityGradientBanner communityId={community?.id} />

      {/* Profile Section - Avatar overlapping banner */}
      <View style={styles.profileSection}>
        <View style={styles.avatarAndActions}>
          <View style={styles.avatarContainer}>
            <ProfileImg
              userId={community?.id || ''}
              imgSize={isMobile ? 'medium' : 'large'}
              styleSize={isMobile ? 80 : 100}
              profile={{
                first_name: community?.name,
                last_name: '',
                photos: community?.photos,
              }}
              style={styles.avatarImage}
            />
          </View>
          <View style={styles.actionButtons}>
            <CommunityFollowButton communityId={community?.id} />
          </View>
        </View>

        {/* Community Name */}
        <TextBold style={styles.communityName}>
          {isLoadingCommunity ? 'Loading...' : (community?.name ?? '')}
        </TextBold>

        {/* Community Headline/Description */}
        {community?.headline && (
          <TextReg style={styles.headline}>{community.headline}</TextReg>
        )}

        {/* Social Links */}
        {hasSocialLinks && (
          <View style={styles.socialLinksContainer}>
            <ProfileSiteButtons
              siteLinks={community?.links}
              userId={community?.id}
            />
          </View>
        )}

        {/* Manage Button - Only visible to owners/editors */}
        {canManageCommunity && (
          <Button
            size="small"
            variant={ButtonVariants.WHITE}
            onPress={handleManagePress}
            style={styles.manageButton}
          >
            Manage
          </Button>
        )}
      </View>

      {/* Bio Section */}
      {community?.bio && (
        <View style={styles.bioContainer}>
          <ExpandableText text={community.bio} charLimit={350} />
        </View>
      )}
    </View>
  );
}

export const CommunityHeader = forwardRef<View, CommunityHeaderProps>(
  CommunityHeaderComponent,
);

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      width: '100%',
      maxWidth: isMobile ? undefined : 820,
      alignSelf: 'center',
    },
    profileSection: {
      width: '100%',
      alignItems: 'flex-start',
      marginTop: isMobile ? -40 : -50,
      paddingHorizontal: isMobile ? 16 : 20,
      gap: 12,
      zIndex: 1,
    },
    avatarAndActions: {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    avatarContainer: {
      borderWidth: 3,
      borderColor: Colors.dark.purple0,
      borderRadius: isMobile ? 44 : 54,
      backgroundColor: Colors.dark.purple0,
    },
    avatarImage: {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    actionButtons: {
      flexDirection: 'row',
      gap: 8,
      marginTop: isMobile ? 48 : 58,
    },
    communityName: {
      fontSize: isMobile ? 24 : 28,
      color: Colors.dark.text,
    },
    headline: {
      fontSize: isMobile ? 14 : 16,
      color: Colors.dark.text,
      opacity: 0.8,
      lineHeight: isMobile ? 20 : 24,
    },
    socialLinksContainer: {
      marginTop: 4,
    },
    manageButton: {
      marginTop: 4,
    },
    bioContainer: {
      width: '100%',
      paddingHorizontal: isMobile ? 16 : 20,
      paddingTop: 16,
    },
  });
